import { useCallback, useEffect, useRef, useState } from "react";
import { useRefreshOnReconnect } from "@/hooks/use-backend-connectivity";
import { fetchWithAuth } from "@/lib/utils";
import i18n from "@/lib/i18n";
import { cacheScope, clearCacheOnAuthFailure, invalidateCache, isProjectLiveConfirmed, readCache, writeCache } from "@/lib/ui-cache";
import { taskPreview, restoreTaskPreview, type CachedTask } from "@/lib/ui-cache-shapes";
import { useCacheScope } from "@/hooks/use-cache-scope";
import { getTaskChange, TASK_CHANGED_EVENT } from "@/lib/project-events";
import type {
  Task,
  TaskCreateRequest,
  TaskListResponse,
  TaskStatus,
  TaskTitleSource,
  TaskUpdateRequest,
} from "@/lib/project-types";
export { TASK_STATUSES } from "@/lib/project-types";
export type { TaskStatus } from "@/lib/project-types";

const PROJECTS_API_BASE = "/api/projects";
const TASKS_API_BASE = "/api/tasks";
const TASKS_PAGE_SIZE = 100;
const POLL_INTERVAL_MS = 5000;
const POLL_MAX_BACKOFF_MS = 60_000;

const jsonHeaders: Record<string, string> = {
  "Content-Type": "application/json",
};

function nextPollBackoffMs(consecutiveFailures: number): number {
  return Math.min(
    POLL_INTERVAL_MS * (2 ** consecutiveFailures),
    POLL_MAX_BACKOFF_MS,
  );
}

function logTaskRequestError(
  method: string,
  url: string,
  error: unknown,
): void {
  console.error("[tasks] request failed", { method, url, error });
}

type TasksByProject = Record<string, Task[]>;
export type TaskFetcher = typeof fetchWithAuth;

export interface TaskSessionBindResult {
  task: Task | null;
  /** HTTP status for a definite server-side rejection; null for transport errors. */
  status: number | null;
}

export interface TaskCreateResult {
  task: Task | null;
  /** HTTP status for a rejected create; null for transport errors. */
  status: number | null;
}

export interface TaskSessionStartResult {
  task: Task | null;
  /** True only when an older server rejected the create-if-unbound extension. */
  needsLegacyFallback: boolean;
}

export interface UseTasksReturn {
  /** Each listed project's tasks, keyed by project id. */
  tasks: TasksByProject;
  /** Project ids whose initial task list has resolved successfully. */
  loadedProjectIds: Set<string>;
  loading: boolean;
  error: string | null;
  list: (projectId: string, status?: TaskStatus) => Promise<Task[] | null>;
  getBySession: (sessionId: string) => Promise<Task | null>;
  create: (
    projectId: string,
    title: string,
    assignedTo?: string | null,
    tags?: string[],
    description?: string | null,
    titleSource?: TaskTitleSource,
    status?: TaskStatus,
    dueDate?: string | null,
  ) => Promise<Task | null>;
  /** Create a task while binding it to an existing conversation session. */
  createWithSession: (
    projectId: string,
    sessionId: string,
    title: string,
    description?: string | null,
  ) => Promise<TaskCreateResult>;
  createSubtask: (
    projectId: string,
    parentTaskId: string,
    title: string,
    idempotencyKey: string,
    titleSource?: TaskTitleSource,
  ) => Promise<TaskCreateResult>;
  update: (taskId: string, input: TaskUpdateRequest) => Promise<Task | null>;
  removeArtifact: (taskId: string, path: string) => Promise<Task | null>;
  getArtifactDownloadLink: (taskId: string, path: string) => Promise<{ url: string; filename: string; media_type: string | null } | null>;
  getArtifactRawUrl: (taskId: string, path: string) => string;
  fetchArtifactRaw: (taskId: string, path: string) => Promise<Blob | null>;
  bindSession: (taskId: string, sessionId: string) => Promise<Task | null>;
  /** Bind while retaining the failure status for safe client-side cleanup. */
  bindSessionDetailed: (taskId: string, sessionId: string) => Promise<TaskSessionBindResult>;
  /** Atomically create and bind the first session when the server supports it. */
  createSessionIfUnbound: (taskId: string) => Promise<TaskSessionStartResult>;
  unbindSession: (taskId: string) => Promise<boolean>;
  refresh: () => Promise<void>;
}

function taskListUrl(projectId: string, status?: TaskStatus, before?: string | null): string {
  const params = new URLSearchParams();
  params.set("limit", String(TASKS_PAGE_SIZE));
  if (status) params.set("status", status);
  if (before) params.set("before", before);
  return `${PROJECTS_API_BASE}/${encodeURIComponent(projectId)}/tasks?${params}`;
}

function latestUpdatedAt(tasks: Task[]): string | null {
  return tasks.reduce<string | null>(
    (latest, task) => (!latest || task.updated_at > latest ? task.updated_at : latest),
    null,
  );
}

/**
 * Manage task lists, retaining one list per project. When `projectId` is set,
 * its list is refreshed silently when the newest task `updated_at` changes.
 */
export function useTasks(
  getToken: () => Promise<string>,
  projectId: string | null = null,
  fetcher: TaskFetcher = fetchWithAuth,
): UseTasksReturn {
  const verifiedScope = useCacheScope();
  const [tasks, setTasks] = useState<TasksByProject>({});
  const liveProjectIdsRef = useRef(new Set<string>());
  const contextKeyRef = useRef(`${verifiedScope}:${projectId}`);
  const [loadedProjectIds, setLoadedProjectIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const tasksLoadedRef = useRef(false);
  const errorRef = useRef<string | null>(null);
  const activeProjectIdRef = useRef<string | null>(projectId);
  const activeStatusRef = useRef<TaskStatus | undefined>(undefined);
  const activeUrlRef = useRef<string | null>(
    projectId ? taskListUrl(projectId) : null,
  );
  const pollAbortRef = useRef<AbortController | null>(null);
  const pollFailureCountRef = useRef(0);
  const pollRetryAtRef = useRef(0);
  const updatedAtByUrlRef = useRef<Record<string, string | null>>({});
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  const setHookError = useCallback((message: string | null) => {
    errorRef.current = message;
    setError(message);
  }, []);

  const replaceTask = useCallback((task: Task) => {
    setTasks((current) => {
      const projectTasks = current[task.project_id];
      if (!projectTasks?.some((item) => item.id === task.id)) return current;

      return {
        ...current,
        [task.project_id]: projectTasks.map((item) => (item.id === task.id ? task : item)),
      };
    });
  }, []);

  const list = useCallback(
    async (requestedProjectId: string, status?: TaskStatus): Promise<Task[] | null> => {
      const requestedScope = cacheScope();
      const url = taskListUrl(requestedProjectId, status);
      const wasActive = requestedProjectId === activeProjectIdRef.current;
      if (wasActive) {
        activeStatusRef.current = status;
        activeUrlRef.current = url;
      }

      const isFirstLoad = !tasksLoadedRef.current;
      if (isFirstLoad) setLoading(true);
      setHookError(null);

      try {
        const res = await fetcherRef.current(url, { headers: {} }, getToken);
        if (!res.ok) {
          if (res.status === 401 || res.status === 403) {
            clearCacheOnAuthFailure();
            setTasks((current) => { const next = { ...current }; delete next[requestedProjectId]; return next; });
            setLoadedProjectIds((current) => { const next = new Set(current); next.delete(requestedProjectId); return next; });
            void invalidateCache("taskPages");
          }
          throw new Error(`HTTP ${res.status}: ${res.statusText}`);
        }

        const firstPage: TaskListResponse = await res.json();
        const allTasks = [...firstPage.tasks];
        const seenCursors = new Set<string>();
        let cursor = firstPage.has_more ? firstPage.next_cursor : null;
        while (cursor && !seenCursors.has(cursor)) {
          seenCursors.add(cursor);
          const pageResponse = await fetcherRef.current(taskListUrl(requestedProjectId, status, cursor), { headers: {} }, getToken);
          if (!pageResponse.ok) throw new Error(`HTTP ${pageResponse.status}: ${pageResponse.statusText}`);
          const page: TaskListResponse = await pageResponse.json();
          allTasks.push(...page.tasks);
          cursor = page.has_more ? page.next_cursor : null;
        }
        const data: TaskListResponse = { ...firstPage, tasks: allTasks };
        if (requestedScope !== cacheScope()) return null;
        liveProjectIdsRef.current.add(requestedProjectId);
        if (!status) void writeCache("taskPages", "general", `${requestedProjectId}:general`, { tasks: data.tasks.slice(0, 100).map(taskPreview).filter((row): row is CachedTask => !!row), has_more: data.has_more === true, next_cursor: data.next_cursor ?? null });
        const supersededActiveRequest =
          wasActive &&
          (activeProjectIdRef.current !== requestedProjectId || activeUrlRef.current !== url);

        if (!supersededActiveRequest) {
          setTasks((current) => ({ ...current, [requestedProjectId]: data.tasks }));
          setLoadedProjectIds((current) => {
            if (current.has(requestedProjectId)) return current;
            const next = new Set(current);
            next.add(requestedProjectId);
            return next;
          });
          updatedAtByUrlRef.current[url] = latestUpdatedAt(data.tasks);
        }
        tasksLoadedRef.current = true;
        return data.tasks;
      } catch (err: unknown) {
        logTaskRequestError("GET", url, err);
        const supersededActiveRequest =
          wasActive &&
          (activeProjectIdRef.current !== requestedProjectId || activeUrlRef.current !== url);
        if (!supersededActiveRequest) {
          setHookError(err instanceof Error ? err.message : i18n.t("projects:taskLoadFailed"));
        }
        return null;
      } finally {
        if (isFirstLoad) setLoading(false);
      }
    },
    [getToken, setHookError],
  );

  const getBySession = useCallback(
    async (sessionId: string): Promise<Task | null> => {
      setHookError(null);
      const url = `${TASKS_API_BASE}/by-session/${encodeURIComponent(sessionId)}`;
      try {
        const res = await fetcherRef.current(
          url,
          { headers: {} },
          getToken,
        );
        // A session can keep its task-session metadata after a task has been
        // unbound or removed. Treat that as a normal no-task result.
        if (res.status === 404) return null;
        if (!res.ok) throw new Error(`HTTP ${res.status}: ${res.statusText}`);

        const task: Task = await res.json();
        replaceTask(task);
        return task;
      } catch (err: unknown) {
        logTaskRequestError("GET", url, err);
        setHookError(err instanceof Error ? err.message : i18n.t("projects:taskLookupFailed"));
        return null;
      }
    },
    [getToken, replaceTask, setHookError],
  );

  const poll = useCallback(
    async (requestedProjectId: string, url: string): Promise<void> => {
      if (
        errorRef.current
        || activeUrlRef.current !== url
        || Date.now() < pollRetryAtRef.current
      ) return;

      pollAbortRef.current?.abort();
      const controller = new AbortController();
      pollAbortRef.current = controller;

      try {
        const res = await fetcherRef.current(
          url,
          { headers: {}, signal: controller.signal },
          getToken,
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}: ${res.statusText}`);

        const data: TaskListResponse = await res.json();
        if (controller.signal.aborted || activeUrlRef.current !== url) return;

        pollFailureCountRef.current = 0;
        pollRetryAtRef.current = 0;

        const updatedAt = latestUpdatedAt(data.tasks);
        if (updatedAtByUrlRef.current[url] === updatedAt) return;

        updatedAtByUrlRef.current[url] = updatedAt;
        setTasks((current) => ({ ...current, [requestedProjectId]: [
          ...data.tasks,
          ...(current[requestedProjectId] ?? []).filter((task) => !data.tasks.some((fresh) => fresh.id === task.id)),
        ] }));
        if (!activeStatusRef.current) void writeCache("taskPages", "general", `${requestedProjectId}:general`, { tasks: data.tasks.slice(0, 100).map(taskPreview).filter((row): row is CachedTask => !!row), has_more: data.has_more === true, next_cursor: data.next_cursor ?? null });
      } catch (err: unknown) {
        if (controller.signal.aborted) return;
        pollFailureCountRef.current += 1;
        const retryInMs = nextPollBackoffMs(pollFailureCountRef.current);
        pollRetryAtRef.current = Date.now() + retryInMs;
        console.warn("[tasks] poll failed", {
          method: "GET",
          url,
          error: err,
          retryInMs,
        });
      }
    },
    [getToken],
  );

  const createTask = useCallback(
    async (
      requestedProjectId: string,
      body: TaskCreateRequest,
      idempotencyKey?: string,
    ): Promise<TaskCreateResult> => {
      setHookError(null);
      const url = `${PROJECTS_API_BASE}/${encodeURIComponent(requestedProjectId)}/tasks`;
      try {
        const res = await fetcherRef.current(
          url,
          { method: "POST", headers: { ...jsonHeaders, ...(idempotencyKey ? { "Idempotency-Key": idempotencyKey } : {}) }, body: JSON.stringify(body) },
          getToken,
        );
        if (!res.ok) {
          const error = new Error(`HTTP ${res.status}: ${res.statusText}`);
          logTaskRequestError("POST", url, error);
          setHookError(error.message);
          return { task: null, status: res.status };
        }

        const task: Task = await res.json();
        setTasks((current) => {
          const currentTasks = current[requestedProjectId] ?? [];
          if (currentTasks.some((item) => item.id === task.id)) return current;

          const activeStatus =
            requestedProjectId === activeProjectIdRef.current
              ? activeStatusRef.current
              : undefined;
          if (activeStatus && task.status !== activeStatus) return current;

          return { ...current, [requestedProjectId]: [task, ...currentTasks] };
        });
        void invalidateCache("taskPages");
        return { task, status: null };
      } catch (err: unknown) {
        logTaskRequestError("POST", url, err);
        setHookError(err instanceof Error ? err.message : i18n.t("projects:taskCreateFailed"));
        return { task: null, status: null };
      }
    },
    [getToken, setHookError],
  );

  const create = useCallback(
    async (
      requestedProjectId: string,
      title: string,
      assignedTo?: string | null,
      tags?: string[],
      description?: string | null,
      titleSource?: TaskTitleSource,
      status?: TaskStatus,
      dueDate?: string | null,
    ): Promise<Task | null> => createTask(requestedProjectId, {
      title,
      assigned_to: assignedTo ?? null,
      ...(tags && tags.length > 0 ? { tags } : {}),
      ...(description !== undefined ? { description } : {}),
      ...(titleSource !== undefined ? { title_source: titleSource } : {}),
      ...(status !== undefined ? { status } : {}),
      ...(dueDate !== undefined ? { due_date: dueDate } : {}),
    }).then((result) => result.task),
    [createTask],
  );

  const createWithSession = useCallback(
    async (
      requestedProjectId: string,
      sessionId: string,
      title: string,
      description: string | null = null,
    ): Promise<TaskCreateResult> => createTask(requestedProjectId, {
      title,
      description,
      status: "open",
      session_id: sessionId,
    }),
    [createTask],
  );

  const createSubtask = useCallback((
    requestedProjectId: string,
    parentTaskId: string,
    title: string,
    idempotencyKey: string,
    titleSource?: TaskTitleSource,
  ) => createTask(requestedProjectId, {
    title,
    parent_task_id: parentTaskId,
    ...(titleSource !== undefined ? { title_source: titleSource } : {}),
  }, idempotencyKey), [createTask]);

  const update = useCallback(
    async (taskId: string, input: TaskUpdateRequest): Promise<Task | null> => {
      setHookError(null);
      const url = `${TASKS_API_BASE}/${encodeURIComponent(taskId)}`;
      try {
        const res = await fetcherRef.current(
          url,
          { method: "PATCH", headers: jsonHeaders, body: JSON.stringify(input) },
          getToken,
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}: ${res.statusText}`);

        const task: Task = await res.json();
        // Older servers may accept a PATCH while silently ignoring fields they
        // do not know. Treat that as a failed save so the editor keeps its draft.
        const fieldsApplied = (
          (input.title === undefined || task.title === input.title)
          && (input.description === undefined || task.description === input.description)
          && (input.status === undefined || task.status === input.status)
          && (input.due_date === undefined || task.due_date === input.due_date)
          && (input.assigned_to === undefined || task.assigned_to === (input.assigned_to || null))
          && (input.tags === undefined || (Array.isArray(task.tags)
            && task.tags.length === input.tags.length
            && input.tags.every((id) => task.tags.some((tag) => tag.id === id))))
        );
        if (!fieldsApplied) throw new Error("Task update was not applied by server");
        replaceTask(task);
        void invalidateCache("taskPages");
        return task;
      } catch (err: unknown) {
        logTaskRequestError("PATCH", url, err);
        setHookError(err instanceof Error ? err.message : i18n.t("projects:taskUpdateFailed"));
        return null;
      }
    },
    [getToken, replaceTask, setHookError],
  );

  const removeArtifact = useCallback(async (taskId: string, path: string): Promise<Task | null> => {
    setHookError(null);
    const url = `${TASKS_API_BASE}/${encodeURIComponent(taskId)}/artifacts`;
    try {
      const res = await fetcherRef.current(url, {
        method: "DELETE", headers: jsonHeaders, body: JSON.stringify({ path }),
      }, getToken);
      if (!res.ok) throw new Error(`HTTP ${res.status}: ${res.statusText}`);
      const task: Task = await res.json();
      replaceTask(task);
      void invalidateCache("taskPages");
      return task;
    } catch (err: unknown) {
      logTaskRequestError("DELETE", url, err);
      setHookError(err instanceof Error ? err.message : i18n.t("projects:taskUpdateFailed"));
      return null;
    }
  }, [getToken, replaceTask, setHookError]);

  const getArtifactDownloadLink = useCallback(async (taskId: string, path: string) => {
    const url = `${TASKS_API_BASE}/${encodeURIComponent(taskId)}/artifacts/download-links`;
    try {
      const res = await fetcherRef.current(url, {
        method: "POST", headers: jsonHeaders, body: JSON.stringify({ path }),
      }, getToken);
      if (!res.ok) throw new Error(`HTTP ${res.status}: ${res.statusText}`);
      return await res.json() as { url: string; filename: string; media_type: string | null };
    } catch (err: unknown) {
      logTaskRequestError("POST", url, err);
      return null;
    }
  }, [getToken]);

  const getArtifactRawUrl = useCallback((taskId: string, path: string): string =>
    `${TASKS_API_BASE}/${encodeURIComponent(taskId)}/artifacts/raw?path=${encodeURIComponent(path)}`, []);

  const fetchArtifactRaw = useCallback(async (taskId: string, path: string): Promise<Blob | null> => {
    // A missing raw route on an older server is expected; the caller can mint a signed link.
    try {
      const res = await fetcherRef.current(getArtifactRawUrl(taskId, path), { method: "GET" }, getToken);
      return res.ok ? await res.blob() : null;
    } catch {
      return null;
    }
  }, [getArtifactRawUrl, getToken]);

  const bindSessionDetailed = useCallback(
    async (taskId: string, sessionId: string): Promise<TaskSessionBindResult> => {
      setHookError(null);
      const url = `${TASKS_API_BASE}/${encodeURIComponent(taskId)}/session`;
      try {
        const res = await fetcherRef.current(
          url,
          {
            method: "PUT",
            headers: jsonHeaders,
            body: JSON.stringify({ session_id: sessionId }),
          },
          getToken,
        );
        if (!res.ok) {
          const error = new Error(`HTTP ${res.status}: ${res.statusText}`);
          logTaskRequestError("PUT", url, error);
          setHookError(error.message);
          return { task: null, status: res.status };
        }

        const task: Task = await res.json();
        replaceTask(task);
        void invalidateCache("taskPages");
        return { task, status: null };
      } catch (err: unknown) {
        logTaskRequestError("PUT", url, err);
        setHookError(err instanceof Error ? err.message : i18n.t("projects:taskBindFailed"));
        return { task: null, status: null };
      }
    },
    [getToken, replaceTask, setHookError],
  );

  const bindSession = useCallback(
    async (taskId: string, sessionId: string): Promise<Task | null> => (
      await bindSessionDetailed(taskId, sessionId)
    ).task,
    [bindSessionDetailed],
  );

  const createSessionIfUnbound = useCallback(
    async (taskId: string): Promise<TaskSessionStartResult> => {
      setHookError(null);
      const url = `${TASKS_API_BASE}/${encodeURIComponent(taskId)}/session`;
      try {
        const res = await fetcherRef.current(
          url,
          {
            method: "PUT",
            headers: jsonHeaders,
            body: JSON.stringify({ create_if_unbound: true }),
          },
          getToken,
        );
        // Older servers require session_id and ignore the unknown extension,
        // producing FastAPI's normal 422. Let the caller use its legacy
        // create-then-bind sequence only in that known compatibility case.
        if (res.status === 422) return { task: null, needsLegacyFallback: true };
        if (!res.ok) {
          const error = new Error(`HTTP ${res.status}: ${res.statusText}`);
          logTaskRequestError("PUT", url, error);
          setHookError(error.message);
          return { task: null, needsLegacyFallback: false };
        }

        const task: Task = await res.json();
        replaceTask(task);
        void invalidateCache("taskPages");
        return { task, needsLegacyFallback: false };
      } catch (err: unknown) {
        logTaskRequestError("PUT", url, err);
        setHookError(err instanceof Error ? err.message : i18n.t("projects:taskSessionStartFailed"));
        return { task: null, needsLegacyFallback: false };
      }
    },
    [getToken, replaceTask, setHookError],
  );

  const unbindSession = useCallback(
    async (taskId: string): Promise<boolean> => {
      setHookError(null);
      const url = `${TASKS_API_BASE}/${encodeURIComponent(taskId)}/session`;
      try {
        const res = await fetcherRef.current(
          url,
          { method: "DELETE", headers: {} },
          getToken,
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}: ${res.statusText}`);

        setTasks((current) => {
          let changed = false;
          const next = Object.fromEntries(
            Object.entries(current).map(([id, projectTasks]) => {
              const updatedTasks = projectTasks.map((task) => {
                if (task.id !== taskId) return task;
                changed = true;
                return { ...task, session_id: null };
              });
              return [id, updatedTasks];
            }),
          ) as TasksByProject;
          return changed ? next : current;
        });
        void invalidateCache("taskPages");
        return true;
      } catch (err: unknown) {
        logTaskRequestError("DELETE", url, err);
        setHookError(err instanceof Error ? err.message : i18n.t("projects:taskUnbindFailed"));
        return false;
      }
    },
    [getToken, setHookError],
  );

  const refresh = useCallback(async (): Promise<void> => {
    const activeProjectId = activeProjectIdRef.current;
    if (!activeProjectId) return;
    await list(activeProjectId, activeStatusRef.current);
  }, [list]);

  useRefreshOnReconnect(refresh, Boolean(projectId));

  useEffect(() => {
    activeProjectIdRef.current = projectId;
    const contextKey = `${verifiedScope}:${projectId}`;
    if (contextKeyRef.current !== contextKey) {
      contextKeyRef.current = contextKey;
      liveProjectIdsRef.current.clear();
      setLoadedProjectIds(new Set());
      setTasks({});
    }
    activeStatusRef.current = undefined;
    const url = projectId ? taskListUrl(projectId) : null;
    activeUrlRef.current = url;
    pollAbortRef.current?.abort();
    pollFailureCountRef.current = 0;
    pollRetryAtRef.current = 0;

    if (!projectId) return;

    let cancelled = false;
    if (verifiedScope) void readCache<{ tasks: CachedTask[] }>("taskPages", "general", `${projectId}:general`).then((snapshot) => {
      if (cancelled || cacheScope() !== verifiedScope || !isProjectLiveConfirmed(projectId) || liveProjectIdsRef.current.has(projectId) || !Array.isArray(snapshot?.tasks)) return;
      const preview = snapshot.tasks.map(restoreTaskPreview).filter((row): row is Task => !!row && row.project_id === projectId);
      if (preview.length) setTasks({ [projectId]: preview });
    });

    void list(projectId);
    return () => {
      cancelled = true;
      pollAbortRef.current?.abort();
      if (activeUrlRef.current === url) activeUrlRef.current = null;
    };
  }, [projectId, list, verifiedScope]);

  useEffect(() => {
    if (!projectId) return;

    const intervalId = setInterval(() => {
      const activeProjectId = activeProjectIdRef.current;
      const url = activeUrlRef.current;
      if (!activeProjectId || !url || errorRef.current) return;
      void poll(activeProjectId, url);
    }, POLL_INTERVAL_MS);

    return () => {
      clearInterval(intervalId);
      pollAbortRef.current?.abort();
    };
  }, [projectId, poll]);

  // Task lists are owned by separate views (for example the project sidebar
  // and a project workspace). Re-list the active project after another view
  // confirms a task mutation so an expanded folder does not wait for polling.
  useEffect(() => {
    if (typeof window === "undefined") return;

    const handleTaskChanged = (event: Event) => {
      const task = getTaskChange(event);
      const activeProjectId = activeProjectIdRef.current;
      if (!task || !activeProjectId || task.project_id !== activeProjectId) return;
      void list(activeProjectId, activeStatusRef.current);
    };

    window.addEventListener(TASK_CHANGED_EVENT, handleTaskChanged);
    return () => window.removeEventListener(TASK_CHANGED_EVENT, handleTaskChanged);
  }, [list]);

  return {
    tasks,
    loadedProjectIds,
    loading,
    error,
    list,
    getBySession,
    create,
    createWithSession,
    createSubtask,
    update,
    removeArtifact,
    getArtifactDownloadLink,
    getArtifactRawUrl,
    fetchArtifactRaw,
    bindSession,
    bindSessionDetailed,
    createSessionIfUnbound,
    unbindSession,
    refresh,
  };
}
